/** Form half of the EscalationRoutesCard — extracted to honor CC-10. */
import { Save, Trash2, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { STAGE_LABELS, STAGE_ORDER } from '@/types/enums';
import type { EscalationRoute } from '@/pages/escalations/types';

export interface ProfileLite {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
}

export function RouteRow({
  r, userById, busy, onEdit, onDelete,
}: {
  r: EscalationRoute;
  userById: Map<string, ProfileLite>;
  busy: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="rounded border px-3 py-2 text-sm space-y-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium">{r.name}</span>
        {!r.active && <Badge variant="outline" className="text-[10px]">inactive</Badge>}
        {r.match_stage && (
          <Badge variant="outline" className="text-[10px]">
            stage: {STAGE_LABELS[r.match_stage as keyof typeof STAGE_LABELS] || r.match_stage}
          </Badge>
        )}
        {r.match_flag && (
          <Badge variant="outline" className="text-[10px]">flag: {r.match_flag}</Badge>
        )}
        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} disabled={busy}>
            <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />
          </Button>
        </div>
      </div>
      {r.description && <p className="text-xs text-muted-foreground">{r.description}</p>}
      <p className="text-xs text-muted-foreground">
        Recipients: {r.recipient_user_ids.map((id) => userById.get(id)?.email ?? id).join(', ')}
      </p>
    </div>
  );
}

export interface RouteFormState {
  name: string;
  description: string;
  match_stage: string;
  match_flag: string;
  recipient_user_ids: string[];
  active: boolean;
}

interface Props {
  isNew: boolean;
  users: ProfileLite[];
  form: RouteFormState;
  setForm: (updater: (f: RouteFormState) => RouteFormState) => void;
  busy: boolean;
  onCancel: () => void;
  onSave: () => void;
}

export function EscalationRouteForm({
  isNew, users, form, setForm, busy, onCancel, onSave,
}: Props) {
  function toggleRecipient(uid: string) {
    setForm((f) => ({
      ...f,
      recipient_user_ids: f.recipient_user_ids.includes(uid)
        ? f.recipient_user_ids.filter((x) => x !== uid)
        : [...f.recipient_user_ids, uid],
    }));
  }

  return (
    <div className="rounded border bg-muted/30 p-3 space-y-2 mt-3">
      <p className="text-sm font-medium">{isNew ? 'New route' : 'Edit route'}</p>
      <input
        type="text" placeholder="Route name"
        value={form.name}
        onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); }}
        className="w-full text-sm rounded border px-2 py-1 bg-background"
      />
      <input
        type="text" placeholder="Description (optional)"
        value={form.description}
        onChange={(e) => { setForm((f) => ({ ...f, description: e.target.value })); }}
        className="w-full text-sm rounded border px-2 py-1 bg-background"
      />
      <div className="grid grid-cols-2 gap-2">
        <select
          value={form.match_stage}
          onChange={(e) => { setForm((f) => ({ ...f, match_stage: e.target.value })); }}
          className="text-sm rounded border px-2 py-1 bg-background"
        >
          <option value="">Match any stage</option>
          {STAGE_ORDER.map((s) => (
            <option key={s} value={s}>{STAGE_LABELS[s]}</option>
          ))}
        </select>
        <input
          type="text" placeholder="Flag substring (optional)"
          value={form.match_flag}
          onChange={(e) => { setForm((f) => ({ ...f, match_flag: e.target.value })); }}
          className="text-sm rounded border px-2 py-1 bg-background"
        />
      </div>
      <div>
        <p className="text-xs font-medium mb-1">
          Recipients ({form.recipient_user_ids.length})
        </p>
        <div className="max-h-40 overflow-auto border rounded bg-background">
          {users.map((u) => (
            <label key={u.id} className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-muted/50 cursor-pointer">
              <input
                type="checkbox"
                checked={form.recipient_user_ids.includes(u.id)}
                onChange={() => { toggleRecipient(u.id); }}
              />
              <span>{u.full_name ?? u.email}</span>
              <span className="text-muted-foreground ml-auto">{u.role}</span>
            </label>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox" checked={form.active}
          onChange={(e) => { setForm((f) => ({ ...f, active: e.target.checked })); }}
        />
        Active
      </label>
      <div className="flex items-center gap-2 justify-end">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button onClick={onSave} disabled={busy} className="gap-1.5">
          <Save className="h-4 w-4" aria-hidden="true" />
          Save
        </Button>
      </div>
    </div>
  );
}
